import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowLeft, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = axios.create({ baseURL: API, withCredentials: true });

const PAGE_SIZE = 20;

const CLASS_COLORS = {
  "Strong Foundation": "bg-emerald-500/15 text-emerald-300",
  "Some Process Gaps": "bg-amber-500/15 text-amber-300",
  "Significant Improvement Opportunities": "bg-orange-500/15 text-orange-300",
  "High-Priority Process Gaps": "bg-red-500/15 text-red-300",
};
const EMAIL_COLORS = {
  sent: "bg-emerald-500/15 text-emerald-300",
  failed: "bg-red-500/15 text-red-300",
  pending: "bg-amber-500/15 text-amber-300",
  not_requested: "bg-white/10 text-slate-400",
};
const EMAIL_LABELS = { sent: "Sent", failed: "Failed", pending: "Sending", not_requested: "Not requested" };

const chip = (cls, text, testid) => (
  <span data-testid={testid} className={`inline-block rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${cls}`}>{text}</span>
);

export default function ScorecardAdmin() {
  const [data, setData] = useState({ items: [], total: 0, page: 1 });
  const [order, setOrder] = useState("desc");
  const [classification, setClassification] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/scorecard/submissions", {
        params: { order, page, page_size: PAGE_SIZE, ...(classification ? { classification } : {}) },
      });
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, classification, page]);

  const openDetail = async (id) => {
    setDetailLoading(true);
    try {
      const { data } = await http.get(`/admin/scorecard/submissions/${id}`);
      setSelected(data);
    } finally {
      setDetailLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  if (selected) {
    return (
      <div data-testid="sc-detail">
        <button onClick={() => setSelected(null)} data-testid="sc-back-to-list" className="text-sm text-slate-300 hover:text-cyan-300 inline-flex items-center gap-1.5 mb-5">
          <ArrowLeft size={15} /> All submissions
        </button>

        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 mb-5">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="font-heading font-bold text-xl text-white">
              {selected.identified ? (selected.first_name || selected.email) : "Anonymous assessment"}
            </h2>
            {chip(CLASS_COLORS[selected.classification], `${selected.overall_score}/100 · ${selected.classification}`, "sc-detail-classification")}
          </div>
          <p className="text-sm text-slate-400 mt-1.5">
            {new Date(selected.created_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}
            {selected.email && ` · ${selected.email}`}
            {selected.business_type && ` · ${selected.business_type}`}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {chip(EMAIL_COLORS[selected.email_status], `Report: ${EMAIL_LABELS[selected.email_status]}`, "sc-detail-email-status")}
            {chip(selected.marketing_consent ? "bg-cyan-500/15 text-cyan-300" : "bg-white/10 text-slate-400",
              selected.marketing_consent ? "Marketing consent: Yes" : "Marketing consent: No", "sc-detail-consent")}
            {chip(selected.booking_interest ? "bg-indigo-500/20 text-indigo-300" : "bg-white/10 text-slate-400",
              selected.booking_interest ? "Clicked: Book a review" : "No booking click", "sc-detail-booking")}
            {chip(selected.audit_clicked ? "bg-indigo-500/20 text-indigo-300" : "bg-white/10 text-slate-400",
              selected.audit_clicked ? "Clicked: Full audit" : "No audit click", "sc-detail-audit")}
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 mb-5">
          <h3 className="font-heading font-bold text-lg text-white mb-4">Stage scores</h3>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2.5">
            {Object.values(selected.stage_scores).map((s) => (
              <div key={s.label} className="flex items-center justify-between text-sm">
                <span className="text-slate-300">{s.label}</span>
                <span className="font-bold text-white">{s.score}%</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 mb-5">
          <h3 className="font-heading font-bold text-lg text-white mb-4">All eight answers</h3>
          <ol className="space-y-4">
            {selected.qa.map((q, i) => (
              <li key={q.id} data-testid={`sc-answer-${q.id}`}>
                <p className="text-sm font-semibold text-slate-200">{i + 1}. {q.question}</p>
                <p className="text-sm text-cyan-300 mt-1">{q.answer || "—"}</p>
              </li>
            ))}
          </ol>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 mb-5">
          <h3 className="font-heading font-bold text-lg text-white mb-4">Recommendations given</h3>
          <div className="space-y-3">
            {selected.recommendations.map((r, i) => (
              <div key={r.stage} data-testid={`sc-rec-${i}`}>
                <p className="text-xs font-bold uppercase tracking-widest text-indigo-300">Priority {i + 1} · {r.label}</p>
                <p className="text-sm text-slate-200 mt-1 leading-relaxed">{r.recommendation}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
          <h3 className="font-heading font-bold text-lg text-white mb-4">7-day plan given</h3>
          <ol className="space-y-2.5">
            {selected.plan.map((p, i) => (
              <li key={i} className="text-sm text-slate-300 leading-relaxed"><span className="font-bold text-cyan-300 mr-2">Day {i + 1}:</span>{p}</li>
            ))}
          </ol>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="scorecard-admin-panel">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <select
          value={classification}
          onChange={(e) => { setClassification(e.target.value); setPage(1); }}
          data-testid="sc-filter-classification"
          aria-label="Filter by classification"
          className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
        >
          <option value="" className="bg-slate-900">All classifications</option>
          {Object.keys(CLASS_COLORS).map((c) => <option key={c} value={c} className="bg-slate-900">{c}</option>)}
        </select>
        <button
          onClick={() => { setOrder(order === "desc" ? "asc" : "desc"); setPage(1); }}
          data-testid="sc-sort-toggle"
          className="rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-slate-200 hover:border-cyan-400/40 transition-colors duration-200"
        >
          Date: {order === "desc" ? "Newest first" : "Oldest first"}
        </button>
        <span className="text-xs text-slate-500 ml-auto">{data.total} submission{data.total === 1 ? "" : "s"}</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin text-cyan-400" /></div>
      ) : data.items.length === 0 ? (
        <p data-testid="sc-empty" className="text-slate-400 text-sm py-10 text-center">No scorecard submissions yet.</p>
      ) : (
        <div className="space-y-3">
          {data.items.map((s) => (
            <button
              key={s.submission_id}
              onClick={() => openDetail(s.submission_id)}
              data-testid={`sc-row-${s.submission_id}`}
              className="w-full text-left rounded-2xl border border-white/10 bg-white/[0.03] p-4 hover:border-cyan-400/30 transition-colors duration-200"
            >
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <div className="min-w-0">
                  <p className="font-semibold text-white text-sm truncate">
                    {s.identified ? (s.first_name ? `${s.first_name} · ${s.email}` : s.email) : "Anonymous"}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {new Date(s.created_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}
                    {s.business_type && ` · ${s.business_type}`}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2 ml-auto">
                  {chip(CLASS_COLORS[s.classification], `${s.overall_score} · ${s.classification}`)}
                  {chip(EMAIL_COLORS[s.email_status], EMAIL_LABELS[s.email_status])}
                  {s.marketing_consent && chip("bg-cyan-500/15 text-cyan-300", "Consent")}
                  {s.booking_interest && chip("bg-indigo-500/20 text-indigo-300", "Booking")}
                  {s.audit_clicked && chip("bg-indigo-500/20 text-indigo-300", "Audit")}
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-2">Weakest: {s.top_stages.join(" · ")}</p>
            </button>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-6">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            data-testid="sc-prev-page"
            className="inline-flex items-center gap-1 rounded-lg border border-white/10 px-3 py-2 text-sm text-slate-200 disabled:opacity-40 hover:border-cyan-400/40 transition-colors duration-200"
          >
            <ChevronLeft size={15} /> Prev
          </button>
          <span data-testid="sc-page-indicator" className="text-xs text-slate-400">Page {data.page} of {totalPages}</span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            data-testid="sc-next-page"
            className="inline-flex items-center gap-1 rounded-lg border border-white/10 px-3 py-2 text-sm text-slate-200 disabled:opacity-40 hover:border-cyan-400/40 transition-colors duration-200"
          >
            Next <ChevronRight size={15} />
          </button>
        </div>
      )}
      {detailLoading && (
        <div className="fixed inset-0 z-50 bg-[#0B0F19]/60 flex items-center justify-center">
          <Loader2 size={24} className="animate-spin text-cyan-400" />
        </div>
      )}
    </div>
  );
}
